import React from 'react';
import { useIntl } from 'react-intl';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import ValuationAgentMiniWidget from './ValuationAgentMiniWidget';
import { currencySymbol } from '../../../utils/formatters';
import ValuationSummaryWidget from './widgets/ValuationSummaryWidget';

/**
 * AgentInfoWidgets - Interactive widgets that display agent outputs with CTAs
 * Supports multiple widget types for different kinds of agent-generated data
 */

const AgentInfoWidgets = ({ widgets = [], onAction, onActionClick, className = "" }) => {
  // Support both prop names for backwards compatibility
  const actionHandler = onAction || onActionClick;
  if (!widgets || widgets.length === 0) {
    return null;
  }

  const renderWidget = (widget) => {
    switch (widget.type) {
      case 'progress_widget':
        return <ProgressWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'action_widget':
        return <ActionWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'metric_widget':
        return <MetricWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'checklist_widget':
        return <ChecklistWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'stats_widget':
        return <StatsWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'opportunity_widget':
        return <OpportunityWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'comparison_widget':
        return <ComparisonWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'overview_widget':
        return <OverviewWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
      case 'valuation_agent_mini':
        return (
          <ValuationAgentMiniWidget
            key={widget.id || 'valuation_mini'}
            user={widget.user}
            currentUser={widget.currentUser}
            company={widget.company}
            features={widget.features}
            onExpand={widget.onExpand || actionHandler}
            onQuickAction={widget.onQuickAction || actionHandler}
          />
        );
      case 'valuation_summary':
        return (
          <ValuationSummaryWidget
            key={widget.id || 'valuation_summary'}
            data={widget.data}
            onAction={actionHandler}
          />
        );
      default:
        return <DefaultWidget key={widget.id || widget.title} widget={widget} onActionClick={actionHandler} />;
    }
  };

  return (
    <div className={`agent-info-widgets ${className}`} style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      gap: '16px',
      padding: '16px 0'
    }}>
      {widgets.map(renderWidget)}
    </div>
  );
};

// Progress Widget - Shows completion progress with next steps
const ProgressWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;
  const { completion_percentage = 0, status = 'not_started', next_steps = [] } = data;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500';
      case 'in_progress': return 'text-amber-500';
      case 'not_started': return 'text-succession-text-secondary';
      default: return 'text-succession-text-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="w-5 h-5" />;
      case 'in_progress': return <ClockIcon className="w-5 h-5" />;
      default: return <ExclamationTriangleIcon className="w-5 h-5" />;
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-amber-500';
      default: return 'bg-succession-text-secondary';
    }
  };

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
        <div className={`flex items-center gap-2 ${getStatusColor(status)}`}>
          {getStatusIcon(status)}
          <span className="text-sm font-medium capitalize">
            {status.replace('_', ' ')}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-succession-bg-secondary rounded overflow-hidden mb-4">
        <div
          className={`h-full ${getProgressColor(status)} transition-all duration-500`}
          style={{ width: `${completion_percentage}%` }}
        />
      </div>

      <div className="flex justify-between items-center mb-4">
        <span className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.progress' })}</span>
        <span className="text-base font-semibold text-succession-text-primary">
          {completion_percentage}%
        </span>
      </div>

      {/* Next steps */}
      {next_steps.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-2">
            {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.next_steps' })}
          </h4>
          <ul className="list-none p-0 m-0 flex flex-col gap-1">
            {next_steps.slice(0, 3).map((step, index) => (
              <li key={index} className="text-xs text-succession-text-secondary flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-succession-primary" />
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA Button */}
      <button
        onClick={() => onActionClick?.({ type: 'view_progress_details', widget })}
        className="w-full px-3 py-2 bg-succession-primary text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 hover:bg-succession-primary-hover"
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.view_details' })}
        <ArrowRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Action Widget - Quick action buttons
const ActionWidget = ({ widget, onActionClick }) => {
  const { title, actions = [] } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <h3 className="text-base font-semibold mb-4 text-succession-text-primary">
        {title}
      </h3>

      <div className="flex flex-col gap-3">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => onActionClick?.(action)}
            className={`px-4 py-3 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 flex items-center justify-between ${
              action.primary
                ? 'bg-succession-primary text-white hover:bg-succession-primary-hover border-none'
                : 'bg-transparent text-succession-text-primary border border-succession-border hover:bg-succession-bg-secondary'
            }`}
          >
            {action.label}
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        ))}
      </div>
    </div>
  );
};

// Metric Widget - Display key metrics
const MetricWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div className="flex items-center gap-3 mb-4">
        <CurrencyDollarIcon className="w-6 h-6 text-green-500" />
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
      </div>

      {data.estimated_value && (
        <div className="mb-4">
          <div className="text-3xl font-bold text-green-500 mb-1">
            {typeof data.estimated_value === 'number'
              ? `${currencySymbol()}${data.estimated_value.toLocaleString()}`
              : (data.estimated_value.toString().startsWith('$') || data.estimated_value.toString().startsWith('\u00a3'))
                ? data.estimated_value
                : `${currencySymbol()}${data.estimated_value}`}
          </div>
          <div className="text-sm text-succession-text-secondary">
            {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.estimated_practice_value' })}
          </div>
        </div>
      )}

      {data.key_factors && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-succession-text-primary mb-2">
            {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.key_value_factors' })}
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {data.key_factors.map((factor, index) => (
              <span key={index} className="px-3 py-1 bg-succession-bg-secondary text-succession-text-primary rounded-full text-xs font-medium">
                {factor}
              </span>
            ))}
          </div>
        </div>
      )}

      <button
        onClick={() => onActionClick?.({ type: 'detailed_valuation', widget })}
        className="w-full px-3 py-2 bg-green-500 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 hover:bg-green-600"
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.get_detailed_report' })}
        <ArrowRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Checklist Widget - Show completion status of items
const ChecklistWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, items = [] } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div className="flex items-center gap-3 mb-4">
        <DocumentTextIcon className="w-6 h-6 text-amber-500" />
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
      </div>

      <div className="mb-4">
        {items.map((item, index) => (
          <div key={index} className={`flex items-center gap-3 py-2 ${index < items.length - 1 ? 'border-b border-succession-border' : ''}`}>
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${item.completed ? 'bg-green-500' : 'bg-succession-bg-secondary'}`}>
              {item.completed && (
                <CheckCircleIcon className="w-3 h-3 text-white" />
              )}
            </div>
            <span className={`text-sm ${item.completed ? 'text-succession-text-secondary line-through' : 'text-succession-text-primary'}`}>
              {item.label}
            </span>
          </div>
        ))}
      </div>

      <button
        onClick={() => onActionClick?.({ type: 'complete_checklist', widget })}
        className="w-full px-3 py-2 bg-amber-500 text-white border-none rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 flex items-center justify-center gap-2 hover:bg-amber-600"
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.complete_remaining' })}
        <ArrowRightIcon className="w-4 h-4" />
      </button>
    </div>
  );
};

// Stats Widget - Display key statistics
const StatsWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <ChartBarIcon style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {data.active_buyers !== undefined && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
              {data.active_buyers}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.active_buyers' })}</div>
          </div>
        )}
        {data.your_followers !== undefined && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
              {data.your_followers}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.your_followers' })}</div>
          </div>
        )}
        {data.recent_inquiries !== undefined && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
              {data.recent_inquiries}
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.new_messages' })}</div>
          </div>
        )}
      </div>

      <button
        onClick={() => onActionClick?.({ type: 'view_marketplace', widget })}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.view_marketplace' })}
        <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
};

// Opportunity Widget - Show opportunities and trends
const OpportunityWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <ArrowTrendingUpIcon style={{ width: '24px', height: '24px', color: '#8b5cf6' }} />
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
      </div>

      <div style={{ marginBottom: '16px' }}>
        {data.matching_buyers !== undefined && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.matching_buyers' })}</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#8b5cf6' }}>
                {data.matching_buyers}
              </span>
            </div>
          </div>
        )}
        
        {data.similar_listings !== undefined && (
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.similar_listings' })}</span>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#8b5cf6' }}>
                {data.similar_listings}
              </span>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => onActionClick?.({ type: 'explore_opportunities', widget })}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#8b5cf6',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#7c3aed'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#8b5cf6'}
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.explore_opportunities' })}
        <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
};

// Comparison Widget - Compare against benchmarks
const ComparisonWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <h3 className="text-base font-semibold mb-4 text-succession-text-primary">
        {title}
      </h3>

      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
            <span style={{ fontSize: '14px', color: '#d1d5db' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.industry_average' })}</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#9ca3af' }}>
              {data.industry_average}x
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#d1d5db' }}>{intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.your_position' })}</span>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: data.your_position === 'Above average' ? '#10b981' : '#f59e0b' 
            }}>
              {data.your_position}
            </span>
          </div>
        </div>

        {data.improvement_areas && (
          <div>
            <h4 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#d1d5db',
              marginBottom: '8px'
            }}>
              {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.areas_for_improvement' })}
            </h4>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              {data.improvement_areas.map((area, index) => (
                <li key={index} style={{
                  fontSize: '13px',
                  color: '#9ca3af',
                  marginBottom: '4px'
                }}>
                  {area}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <button
        onClick={() => onActionClick?.({ type: 'benchmark_analysis', widget })}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#6366f1',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#4f46e5'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#6366f1'}
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.view_full_analysis' })}
        <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
};

// Overview Widget - General overview
const OverviewWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <SparklesIcon style={{ width: '24px', height: '24px', color: '#06b6d4' }} />
        <h3 className="text-base font-semibold text-succession-text-primary">
          {title}
        </h3>
      </div>

      <div style={{ marginBottom: '16px' }}>
        {data.next_priority && (
          <div style={{
            padding: '16px',
            backgroundColor: '#4b5563',
            borderRadius: '8px',
            border: '1px solid #06b6d4',
            marginBottom: '12px'
          }}>
            <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#06b6d4', margin: '0 0 8px 0' }}>
              {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.next_priority' })}
            </h4>
            <p style={{ fontSize: '14px', color: '#e5e7eb', margin: 0 }}>
              {data.next_priority}
            </p>
          </div>
        )}
      </div>

      <button
        onClick={() => onActionClick?.({ type: 'view_dashboard', widget })}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#06b6d4',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}
        onMouseEnter={(e) => e.target.style.backgroundColor = '#0891b2'}
        onMouseLeave={(e) => e.target.style.backgroundColor = '#06b6d4'}
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.go_to_dashboard' })}
        <ArrowRightIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
};

// Default Widget - Fallback for unknown widget types
const DefaultWidget = ({ widget, onActionClick }) => {
  const intl = useIntl();
  const { title, data } = widget;

  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-lg p-4 text-succession-text-primary">
      <h3 className="text-base font-semibold mb-4 text-succession-text-primary">
        {title}
      </h3>

      <pre style={{
        fontSize: '12px',
        color: '#9ca3af',
        backgroundColor: '#4b5563',
        padding: '12px',
        borderRadius: '8px',
        overflow: 'auto',
        marginBottom: '16px'
      }}>
        {JSON.stringify(data, null, 2)}
      </pre>

      <button
        onClick={() => onActionClick?.(widget)}
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#6b7280',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer'
        }}
      >
        {intl.formatMessage({ id: 'succession.agent_system.agent_info_widgets.view_details' })}
      </button>
    </div>
  );
};

export default AgentInfoWidgets;